import type { RouteTravelMode } from '@features/adventures/types/workflow.types'

/**
 * A single GPS fix logged while recording. `t` is epoch milliseconds.
 * accuracy/elevation come straight from the Geolocation API when available.
 */
export interface TrackPoint {
  lat: number
  lng: number
  accuracy?: number
  elevation?: number
  /**
   * Ground speed in meters/second at this fix: the device-reported value when
   * available, else derived from distance/time since the previous logged point.
   * Persisted for trail review and adventure trail audits on ranked sessions.
   */
  speed?: number
  t: number
}

/**
 * A point of interest the user marked along the way, with an optional note and
 * any number of photos (stored separately as Blobs in the trailPhotos store and
 * referenced here by id).
 */
export interface TrailWaypoint {
  id: string
  lat: number
  lng: number
  label: string
  note: string
  photoIds: string[]
  t: number
  /**
   * What this waypoint is — a `WaypointKind` value (see `utils/waypointKinds.ts`).
   * Optional, and an unrecognised value renders as a plain waypoint: every
   * waypoint recorded before kinds existed has none, and a trail synced from a
   * newer app build may carry one this version has never heard of.
   */
  kind?: string
}

/**
 * A named slice of the recorded track. startIndex/endIndex are inclusive
 * indices into Trail.track and become a single Route node on import. Optional
 * notes + photos describe the section and feed the auto-build-adventure flow;
 * photos are stored as Blobs in the trailPhotos store and referenced by id.
 */
export interface TrailSegment {
  id: string
  name: string
  startIndex: number
  endIndex: number
  travelMode: RouteTravelMode
  notes?: string
  photoIds?: string[]
}

/**
 * A recorded hike. Lives in IndexedDB on the user's device by default. A user
 * can opt a trail in to "sync to my account" (see the sync* fields below), which
 * uploads the full recording to the backend so every device signed in as that
 * user can see and edit it. Photos are stored in a sibling store keyed by id
 * (see TrailPhoto) so the trail record stays light to list.
 */
export interface Trail {
  id: string
  name: string
  createdAt: number
  updatedAt: number
  track: TrackPoint[]
  waypoints: TrailWaypoint[]
  segments: TrailSegment[]

  /** Pedometer steps recorded during the hike. Set by the mobile recorder (the web
   * recorder has no step sensor); the web preserves + displays it when synced. */
  steps?: number

  /**
   * Time actually spent moving, with paused stretches excluded.
   *
   * The wall clock between the first and last track point cannot tell you this — on a
   * hike with a long lunch stop the two differ by the length of lunch — so the recorder
   * accumulates it separately and it travels to the server as its own field. Absent on
   * trails recorded before this existed and on imported GPX; render `movingMs ?? <wall
   * clock>` and only call it "moving time" when it is set.
   */
  movingMs?: number

  /**
   * Cross-device account sync (opt-in per trail). When true, edits are pushed to
   * the backend and the trail appears on the user's other devices.
   * `remoteId` is the server-side id (this trail's key in /api/me/trails).
   * `syncVersion` is the last server Version we saw — sent back as baseVersion on
   * update for optimistic concurrency (a mismatch means another device wrote first).
   * `syncedAt` is the epoch ms of the last successful push/pull.
   * Photo blobs are NOT synced — waypoint photos stay on the device that took them.
   */
  syncEnabled?: boolean
  remoteId?: string
  syncVersion?: number
  syncedAt?: number
}

/** A waypoint photo blob, kept out of the Trail record so lists load cheaply. */
export interface TrailPhoto {
  id: string
  trailId: string
  blob: Blob
  caption?: string
  createdAt: number
  /**
   * Cached compressed data URL used when syncing this photo to the account, so a
   * repeat push doesn't re-compress the (immutable) image. On a pulled photo it's
   * the data URL it was reconstructed from. Not shown in the UI. See sync/trailSync.
   */
  syncDataUrl?: string
}

/**
 * A crash-save checkpoint of an in-progress recording. Persisted to IndexedDB
 * (single record, fixed id) every few seconds while recording so a reload or
 * crash mid-hike can be recovered. Photos/waypoints aren't checkpointed here —
 * waypoint photos are already written to the trailPhotos store as captured, and
 * the recovered track can be re-saved as a fresh trail.
 */
export interface RecordingDraft {
  /** Fixed sentinel key — there is only ever one draft at a time. */
  id: 'current'
  trailId: string
  status: 'recording' | 'paused'
  startedAt: number
  track: TrackPoint[]
  distanceMeters: number
  /** Recording time elapsed so far (excludes paused intervals), in ms. */
  elapsedMs: number
  updatedAt: number
}

/**
 * One timed walk of a saved trail — what you are trying to beat next time.
 *
 * Kept apart from {@link Trail} rather than nested inside it, mirroring the server.
 * A trail syncs last-write-wins, so a stale push adopts the server's copy wholesale;
 * an attempt buried in that payload would be destroyed by a conflict it had nothing
 * to do with. Attempts are append-only and sync on their own, keyed by an id minted
 * here so re-sending one is a no-op rather than a second personal best.
 */
export interface TrailAttempt {
  /** Minted on this device. The basis of idempotent upload — never server-assigned. */
  id: string
  trailId: string
  startedAt: number
  finishedAt: number
  /** Wall clock for the walk, pauses included. */
  elapsedMs: number
  /** Time actually moving. Undefined when it could not be measured. */
  movingMs?: number
  distanceMeters: number
  steps?: number
  /**
   * Share of the trail actually covered, 0-100.
   *
   * Without it the fastest time on any route is whoever starts the clock at the
   * trailhead and stops it ten metres on. Below the ranking threshold an attempt is
   * still kept — it happened — but it sets no personal best.
   */
  coveragePercent: number
  note?: string
  /** Epoch ms of the last successful push; unset means it is still only on this device. */
  syncedAt?: number
}

/** Coverage at or above this ranks for a personal best. Mirrors the server's UserTrailAttempt. */
export const MIN_RANKING_COVERAGE = 90
